import React, { Suspense, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useGallery } from 'component/Accommodation/Gallery/__hooks/useGallery';

import Portal from 'utility/Bind/Portal/Portal';
import ErrorBoundary from 'utility/Debug/ErrorBoundary';
import { lazyRetry } from 'utility/Lazy/Retry';

import { GalleryContext } from './__context';
import useLegacyListener from './__hooks/useLegacyListener';

const LazyGallery = React.lazy(() =>
    lazyRetry(() => import('component/Accommodation/Gallery/Stage'))
);

interface GalleryProps {
    target: string;
}

const Gallery = ({ target }: GalleryProps) => {
    const { t } = useTranslation();
    const { hotelParams } = useContext(GalleryContext);
    const { showGallery, openGallery, closeGallery, onOfferLinkClick } =
        useGallery();

    useLegacyListener();
    useEffect(() => {
        !!hotelParams?.hotelId && openGallery();
    }, [hotelParams?.hotelId, openGallery]);

    return (
        <Portal root={target}>
            {showGallery ? (
                <ErrorBoundary>
                    <Suspense fallback={t('TRANS-loading')}>
                        <LazyGallery
                            step4Url={hotelParams?.step4Url}
                            shareLeafletUrl={
                                // shareLeafletUrl in hotelParams on merkzettel is a shortcut url to the merkzettel itself
                                hotelParams?.step4Url ??
                                hotelParams?.shareLeafletUrl
                            }
                            hotelId={hotelParams?.hotelId ?? 0}
                            closeGallery={closeGallery}
                            onHeaderCtaClick={
                                hotelParams?.callback
                                    ? onOfferLinkClick
                                    : undefined
                            }
                        />
                    </Suspense>
                </ErrorBoundary>
            ) : null}
        </Portal>
    );
};

export default Gallery;
